/**
 * Tenant activo Vault (UUID Datavault) — fuente canónica unificada.
 * Paridad legacy: `localStorage.tenant_id` + match en `user.tenants[]`.
 * En Nebula: JWT post `jwt-select-company` → espejo en localStorage.
 */
import {
  normalizeVaultTenantId,
  resolveVaultActiveTenantIdFromTokenAndStorage,
  extractVaultTenantIdFromTenantsList,
  persistVaultTenantScopeToStorage,
  readVaultTenantIdFromAssignmentRow,
} from './vault-tenant-scope.util';
import {
  readAuthMeCompanyId,
  isSimappeProductCompanyLabel,
} from './vault-nebula-company.util';
import {
  resolveVaultTenantIdByName,
  resolveVaultTenantNameFromCatalog,
} from './vault-tenant-catalog.util';
import {
  persistVaultTenantSnapshot,
  readVaultTenantSnapshot,
} from './vault-tenant-snapshot.util';
import {
  persistVaultTenantAssignmentsToStorage,
  readVaultTenantAssignmentsFromStorage,
} from './vault-tenant-assignments.storage';
import { sessionJwtIndicatesVaultSuperadmin, vaultUserIsSuperadminFromSession } from './vault-user-roles.util';
import { sessionUserIsCanonicalVaultSuperadmin } from './vault-canonical-superadmin.util';
import {
  readVaultIsSuperadminScope,
  readVaultOperativeRole,
  resolveSuperadminFromOperativeScope,
} from './vault-operative-role.util';
import {
  applyVaultSessionPinToStorage,
  readVaultSessionPin,
  vaultSessionPinIsActive,
} from './vault-session-pin.util';

/** Una sola empresa activa por sesión; el usuario puede cambiar cuál está activa. */
export const VAULT_SINGLE_TENANT_MODE = true;

const VAULT_AUTH_ME_TENANTS_KEY = 'vault_auth_me_tenants';
/** true cuando el usuario eligió otra empresa distinta a su asignación principal. */
const VAULT_USER_SWITCHED_TENANT_KEY = 'vault_user_switched_tenant';

export interface VaultTenantRoleAssignment {
  tenant_id: string;
  tenant_name?: string;
  role: string;
}

export interface VaultActiveTenantContext {
  tenantId: string;
  tenantName: string;
  operativeRole: string;
  roleLabel: string;
  companyLabel: string;
  scopeLabel: string;
  isGlobalScope: boolean;
}

function decodeAccessTokenPayload(): Record<string, unknown> | null {
  if (typeof localStorage === 'undefined') return null;
  const token = localStorage.getItem('access_token');
  if (!token || typeof token !== 'string') return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function readVaultUserDataFromLocalStorage(): Record<string, unknown> | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function tenantIdFromLocalStorage(): string {
  if (typeof localStorage === 'undefined') return '';
  return (
    localStorage.getItem('tenant_id')?.trim() ||
    localStorage.getItem('tenantId')?.trim() ||
    localStorage.getItem('selected_tenant_id')?.trim() ||
    ''
  );
}

function readStoredAuthMeTenants(): VaultTenantRoleAssignment[] {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(VAULT_AUTH_ME_TENANTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as VaultTenantRoleAssignment[];
  } catch {
    return [];
  }
}

function rowsFromTenantsArray(tenants: unknown): VaultTenantRoleAssignment[] {
  if (!Array.isArray(tenants)) return [];
  const out: VaultTenantRoleAssignment[] = [];
  for (const t of tenants) {
    if (!t || typeof t !== 'object') continue;
    const o = t as Record<string, unknown>;
    let tenant_id = readVaultTenantIdFromAssignmentRow(o);
    let tenant_name =
      o['tenant_name'] != null
        ? String(o['tenant_name']).trim()
        : o['name'] != null
          ? String(o['name']).trim()
          : undefined;
    const nested = o['tenant'];
    if (nested && typeof nested === 'object') {
      const row = nested as Record<string, unknown>;
      tenant_id = tenant_id || readVaultTenantIdFromAssignmentRow(row);
      if (!tenant_name) {
        tenant_name =
          row['name'] != null
            ? String(row['name']).trim()
            : row['tenant_name'] != null
              ? String(row['tenant_name']).trim()
              : undefined;
      }
    }
    if (!tenant_id && tenant_name) {
      tenant_id = resolveVaultTenantIdByName(tenant_name);
    }
    const role = String(o['role'] ?? '').trim();
    if (tenant_id || role) {
      if (!tenant_id && isSimappeProductCompanyLabel(tenant_name)) continue;
      out.push({ tenant_id, tenant_name, role });
    }
  }
  return out;
}

/** Persiste `tenants[]` de `/api/auth/me` para resolver nombre de empresa en UI. */
export function persistVaultAuthMeTenants(tenants: unknown): void {
  if (!Array.isArray(tenants)) return;
  const incoming = rowsFromTenantsArray(tenants);
  const merged = mergeVaultTenantAssignments(
    [...readStoredAuthMeTenants(), ...readVaultTenantAssignmentsFromStorage()],
    incoming,
  );
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(VAULT_AUTH_ME_TENANTS_KEY, JSON.stringify(merged));
  }
  persistVaultTenantAssignmentsToStorage(merged);
}

function mergeVaultTenantAssignments(
  existing: VaultTenantRoleAssignment[],
  incoming: VaultTenantRoleAssignment[],
): VaultTenantRoleAssignment[] {
  const byId = new Map<string, VaultTenantRoleAssignment>();
  const push = (rows: VaultTenantRoleAssignment[]) => {
    for (const row of rows) {
      const id = normalizeVaultTenantId(row.tenant_id) || '';
      const role = row.role?.trim() || '';
      const tenant_name = (row.tenant_name ?? '').trim();
      if (!id && !tenant_name && !role) continue;
      const key = id || `name:${tenant_name}` || `role:${role}`;
      const prev = byId.get(key);
      byId.set(key, {
        tenant_id: id || prev?.tenant_id || '',
        tenant_name:
          tenant_name && !isSimappeProductCompanyLabel(tenant_name)
            ? tenant_name
            : prev?.tenant_name,
        role: role || prev?.role || '',
      });
    }
  };
  push(existing);
  push(incoming);
  return [...byId.values()].filter(
    (r) =>
      !!normalizeVaultTenantId(r.tenant_id) ||
      (!!(r.tenant_name ?? '').trim() && !isSimappeProductCompanyLabel(r.tenant_name ?? '')),
  );
}

/** Nombre de empresa guardado en sesión para un tenant Vault. */
export function readStoredVaultTenantName(tenantId: string): string {
  const id = (tenantId || '').trim();
  if (!id) return '';

  const snapshot = readVaultTenantSnapshot();
  if (snapshot?.tenant_id === id && snapshot.tenant_name) {
    return snapshot.tenant_name;
  }

  const match = readStoredAuthMeTenants().find((row) => row.tenant_id === id);
  const name = (match?.tenant_name ?? '').trim();
  if (!name || isSimappeProductCompanyLabel(name)) return '';
  return name;
}

/** Persiste tenant activo y nombres desde listado API (`auth/me`, `my-tenants`). */
export function persistVaultTenantFromTenantsPayload(tenants: unknown): string {
  if (tenants == null) return '';
  persistVaultAuthMeTenants(tenants);
  const rows = rowsFromTenantsArray(Array.isArray(tenants) ? tenants : []);
  const tenantId = extractVaultTenantIdFromTenantsList(
    Array.isArray(tenants) ? tenants : [],
  );
  if (tenantId) {
    persistVaultTenantScopeToStorage(tenantId);
    const match = rows.find((r) => r.tenant_id === tenantId);
    persistVaultTenantSnapshot({
      tenant_id: tenantId,
      tenant_name: match?.tenant_name,
      role: match?.role,
    });
  }
  return tenantId;
}

function unwrapAuthMeRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = raw as Record<string, unknown>;
  const nested = root['data'];
  if (nested && typeof nested === 'object') {
    return nested as Record<string, unknown>;
  }
  return root;
}

/** `GET /api/auth/me` → `active_tenant` (objeto o UUID). */
export function readActiveTenantIdFromAuthMeRecord(
  record: Record<string, unknown>,
): string {
  const active = record['active_tenant'];
  if (active && typeof active === 'object') {
    return readVaultTenantIdFromAssignmentRow(active as Record<string, unknown>);
  }
  if (typeof active === 'string' && active.trim()) {
    return normalizeVaultTenantId(active) || active.trim();
  }
  for (const key of ['active_tenant_id', 'activeTenantId'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return normalizeVaultTenantId(value) || value.trim();
    }
  }
  return '';
}

/** Hidrata tenant activo desde `active_tenant` de auth/me si el usuario aún no eligió uno. */
export function persistActiveTenantFromAuthMe(raw: unknown): string {
  const record = unwrapAuthMeRecord(raw);
  if (!record) return '';

  const activeId = readActiveTenantIdFromAuthMeRecord(record);
  if (!activeId) return '';

  if (typeof localStorage !== 'undefined') {
    const existing = normalizeVaultTenantId(
      localStorage.getItem('tenant_id') || localStorage.getItem('tenantId') || '',
    );
    if (existing) return existing;
  }

  persistVaultTenantScopeToStorage(activeId);
  const tenants = record['tenants'];
  const rows = rowsFromTenantsArray(Array.isArray(tenants) ? tenants : []);
  const match = rows.find((row) => normalizeVaultTenantId(row.tenant_id) === activeId);
  persistVaultTenantSnapshot({
    tenant_id: activeId,
    tenant_name: match?.tenant_name,
    role: match?.role,
  });
  return activeId;
}

/** Empresas distintas asignadas al usuario (auth/me + storage). */
export function countVaultTenantAssignments(): number {
  return collectTenantAssignments().filter((row) =>
    normalizeVaultTenantId(row.tenant_id),
  ).length;
}

export function readVaultUserSwitchedTenant(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(VAULT_USER_SWITCHED_TENANT_KEY) === 'true';
}

/** Marca si la sesión opera en una empresa distinta a la asignación principal (requiere `X-Tenant-ID`). */
export function persistVaultUserSwitchedTenant(switched: boolean): void {
  if (typeof localStorage === 'undefined') return;
  if (switched) {
    localStorage.setItem(VAULT_USER_SWITCHED_TENANT_KEY, 'true');
  } else {
    localStorage.removeItem(VAULT_USER_SWITCHED_TENANT_KEY);
  }
}

/**
 * Cabecera `X-Tenant-ID` (DEV 57a9287):
 * - Superadmin con empresa activa, o
 * - Usuario que cambió de empresa activa (solo una a la vez).
 * Usuario con 1 asignación sin cambio: sin cabecera.
 */
export function shouldSendVaultXTenantHeader(): boolean {
  const activeId = resolveVaultActiveTenantId();
  if (!activeId) return false;
  if (readSuperadminScopeFromStorage()) return true;
  return readVaultUserSwitchedTenant();
}

function tenantLinkId(row: Record<string, unknown>): string {
  const explicit = String(row['tenant_id'] ?? row['tenantId'] ?? '').trim();
  if (explicit) {
    return normalizeVaultTenantId(explicit) || explicit;
  }
  const id = String(row['id'] ?? '').trim();
  if (!id) return '';
  const hasCompanyLabel =
    row['name'] != null || row['tenant_name'] != null || row['tenantName'] != null;
  if (hasCompanyLabel) {
    return normalizeVaultTenantId(id) || id;
  }
  return normalizeVaultTenantId(id) || '';
}

/** Cruza `GET /api/auth/me` con `GET /api/tenants/my-tenants` (asignación Vault, no Simappe). */
export function resolveTenantFromAuthMeAndMyTenants(
  me: unknown,
  myTenants: unknown[],
): string {
  if (!myTenants.length) {
    const meTenants = unwrapAuthMeRecord(me)?.['tenants'];
    return extractVaultTenantIdFromTenantsList(
      Array.isArray(meTenants) ? meTenants : [],
    );
  }

  const companyId = readAuthMeCompanyId(me);
  if (companyId) {
    for (const entry of myTenants) {
      if (!entry || typeof entry !== 'object') continue;
      const row = entry as Record<string, unknown>;
      const rowCompanyId = row['company_id'] ?? row['companyId'] ?? row['nebula_company_id'];
      if (rowCompanyId == null) continue;
      if (String(rowCompanyId).trim() !== companyId) continue;
      const tid = tenantLinkId(row);
      if (tid) return tid;
    }
  }

  return extractVaultTenantIdFromTenantsList(myTenants);
}

function collectTenantAssignments(
  extra?: VaultTenantRoleAssignment[],
): VaultTenantRoleAssignment[] {
  const byId = new Map<string, VaultTenantRoleAssignment>();
  const push = (rows: VaultTenantRoleAssignment[]) => {
    for (const row of rows) {
      const id = normalizeVaultTenantId(row.tenant_id);
      if (!id) continue;
      byId.set(id, { ...byId.get(id), ...row, tenant_id: id });
    }
  };
  if (extra?.length) push(extra);
  push(readStoredAuthMeTenants());
  push(readVaultTenantAssignmentsFromStorage());

  const snapshot = readVaultTenantSnapshot();
  if (snapshot?.tenant_id) {
    push([
      {
        tenant_id: snapshot.tenant_id,
        tenant_name: snapshot.tenant_name || undefined,
        role: snapshot.role,
      },
    ]);
  }

  const ud = readVaultUserDataFromLocalStorage();
  if (ud) push(rowsFromTenantsArray(ud['tenants']));

  const payload = decodeAccessTokenPayload();
  if (payload) {
    push(rowsFromTenantsArray(payload['tenants']));
    const user = payload['User'] ?? payload['user'];
    if (user && typeof user === 'object') {
      push(rowsFromTenantsArray((user as Record<string, unknown>)['tenants']));
    }
  }

  return [...byId.values()];
}

function singleTenantIdFromAssignments(assignments: VaultTenantRoleAssignment[]): string {
  const withId = assignments
    .map((a) => normalizeVaultTenantId(a.tenant_id))
    .filter((id): id is string => !!id);
  if (withId.length >= 1 && (withId.length === 1 || VAULT_SINGLE_TENANT_MODE)) {
    return withId[0];
  }
  return '';
}

function tenantIdMatchingOperativeRole(
  assignments: VaultTenantRoleAssignment[],
  operativeRole: string,
): string {
  const roleNorm = normalizeRoleToken(operativeRole);
  if (!roleNorm) return '';
  for (const row of assignments) {
    const tid = (row.tenant_id || '').trim();
    const role = (row.role || '').trim();
    if (!tid || !role) continue;
    if (normalizeRoleToken(role) === roleNorm) {
      const id = normalizeVaultTenantId(tid);
      if (id) return id;
    }
  }
  return '';
}

/**
 * Tenant activo Vault (UUID). Prioridad: asignación Vault (my-tenants / auth/me) → snapshot → JWT.
 * No usa empresa Simappe/Nebula (login); esa es otra capa.
 * Si `tenant_id` en storage ya no está en asignaciones, usa la principal (salvo cambio manual).
 */
export function resolveVaultActiveTenantId(): string {
  const assignments = collectTenantAssignments();
  const fromStorage = normalizeVaultTenantId(resolveVaultActiveTenantIdFromTokenAndStorage());

  if (fromStorage) {
    if (readSuperadminScopeFromStorage() || readVaultUserSwitchedTenant()) {
      return fromStorage;
    }
    const inAssignments = assignments.some(
      (row) => normalizeVaultTenantId(row.tenant_id) === fromStorage,
    );
    if (inAssignments || !assignments.length) {
      return fromStorage;
    }
  }

  const fromAssignments = singleTenantIdFromAssignments(assignments);
  if (fromAssignments) return fromAssignments;

  const snapshotId = normalizeVaultTenantId(readVaultTenantSnapshot()?.tenant_id);
  if (snapshotId) return snapshotId;

  return fromStorage;
}

/** Tras `auth/me`: alinea tenant activo con asignación del servidor si el storage quedó obsoleto. */
export function reconcileVaultActiveTenantFromAuthMe(raw: unknown): string {
  if (vaultSessionPinIsActive()) {
    applyVaultSessionPinToStorage();
    const pin = readVaultSessionPin();
    return normalizeVaultTenantId(pin?.tenant_id ?? '') || (pin?.tenant_id ?? '').trim();
  }

  const record = unwrapAuthMeRecord(raw);
  if (!record) return resolveVaultActiveTenantId();

  const tenants = record['tenants'];
  const rows = rowsFromTenantsArray(Array.isArray(tenants) ? tenants : []);
  if (rows.length) {
    persistVaultAuthMeTenants(rows);
    persistVaultTenantAssignmentsToStorage(rows);
  }

  const activeFromMe = readActiveTenantIdFromAuthMeRecord(record);
  const primary = activeFromMe || singleTenantIdFromAssignments(rows);
  const stored = normalizeVaultTenantId(tenantIdFromLocalStorage());
  const userSwitched = readVaultUserSwitchedTenant();

  if (primary && !userSwitched) {
    const storedValid = stored && rows.some((r) => normalizeVaultTenantId(r.tenant_id) === stored);
    const nextId = storedValid ? stored : primary;
    if (nextId !== stored) {
      persistVaultTenantScopeToStorage(nextId);
      persistVaultUserSwitchedTenant(false);
    }
    const match = rows.find((row) => normalizeVaultTenantId(row.tenant_id) === nextId);
    persistVaultTenantSnapshot({
      tenant_id: nextId,
      tenant_name: match?.tenant_name,
      role: match?.role,
    });
    return nextId;
  }

  return persistActiveTenantFromAuthMe(raw) || resolveVaultActiveTenantId();
}

export function shortVaultTenantId(tenantId: string): string {
  const id = (tenantId || '').trim();
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…`;
}

function vaultTenantNameFromAssignments(
  assignments: VaultTenantRoleAssignment[],
  tenantId?: string,
  operativeRole?: string,
): string {
  const roleNorm = operativeRole ? normalizeRoleToken(operativeRole) : '';
  if (roleNorm) {
    for (const row of assignments) {
      const name = (row.tenant_name ?? '').trim();
      if (!name || isSimappeProductCompanyLabel(name)) continue;
      if (normalizeRoleToken(row.role) === roleNorm) return name;
    }
  }

  if (tenantId) {
    const match = assignments.find((a) => a.tenant_id === tenantId);
    const name = (match?.tenant_name ?? '').trim();
    if (name && !isSimappeProductCompanyLabel(name)) return name;
  }

  for (const row of assignments) {
    const name = (row.tenant_name ?? '').trim();
    if (name && !isSimappeProductCompanyLabel(name)) return name;
  }

  return '';
}

function resolveCompanyLabel(
  tenantId: string,
  assignments: VaultTenantRoleAssignment[],
  operativeRole?: string,
): string {
  const fromAssignments = vaultTenantNameFromAssignments(assignments, tenantId, operativeRole);
  if (fromAssignments) return fromAssignments;

  if (tenantId) {
    const fromCatalog = resolveVaultTenantNameFromCatalog(tenantId);
    if (fromCatalog && !isSimappeProductCompanyLabel(fromCatalog)) return fromCatalog;
    const fromSession = readStoredVaultTenantName(tenantId);
    if (fromSession) return fromSession;
    const match = assignments.find((a) => a.tenant_id === tenantId);
    const name = (match?.tenant_name ?? '').trim();
    if (name && !isSimappeProductCompanyLabel(name)) return name;
    return shortVaultTenantId(tenantId);
  }

  return '';
}

function readOperativeRoleFromStorage(): string {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem('vault_operative_role')?.trim() || '';
}

function readSuperadminScopeFromStorage(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem('vault_is_superadmin_scope') === 'true';
}

/**
 * Rol operativo para el tenant activo (paridad legacy `getCurrentUserRole`).
 * Fuente: asignación `user_tenants` / my-tenants, no caché ni stats globales.
 */
export function resolveOperativeRoleForActiveTenant(
  tenantId: string,
  assignments?: VaultTenantRoleAssignment[],
): string {
  const rows = assignments ?? collectTenantAssignments();
  const id = normalizeVaultTenantId(tenantId);

  if (id) {
    const match = rows.find(
      (a) => normalizeVaultTenantId(a.tenant_id) === id && (a.role ?? '').trim(),
    );
    if (match?.role) return match.role.trim();
  }

  const withRole = rows.filter((a) => (a.role ?? '').trim());
  if (withRole.length === 1) return withRole[0].role!.trim();

  return '';
}

function resolveOperativeRoleForTenant(
  tenantId: string,
  assignments: VaultTenantRoleAssignment[],
): string {
  const fromSession = readVaultOperativeRole();
  if (fromSession) return fromSession;

  const fromAssignment = resolveOperativeRoleForActiveTenant(tenantId, assignments);
  if (fromAssignment) return fromAssignment;

  return readOperativeRoleFromStorage();
}

function normalizeRoleToken(raw: string): string {
  return raw.trim().toLowerCase().replace(/^role_/, '').replace(/[\s-]+/g, '_');
}

function roleLabelFromDashboardFallback(dashboardRole?: string | null): string {
  const trimmed = (dashboardRole ?? '').trim();
  if (!trimmed) return 'Sin rol asignado';
  const norm = normalizeRoleToken(trimmed);
  const labels: Record<string, string> = {
    superadmin: 'Super Administrador',
    admin_empresa: 'Administrador',
    archivista: 'Archivista',
    auditor: 'Auditor',
    consulta: 'Consulta',
  };
  if (labels[norm]) return labels[norm];
  if (norm.includes('superadmin')) return 'Super Administrador';
  return trimmed.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function operativeRoleLabel(raw: string): string {
  return roleLabelFromDashboardFallback(raw);
}

function buildGlobalSuperadminTenantContext(): VaultActiveTenantContext {
  return {
    tenantId: '',
    tenantName: '',
    operativeRole: 'superadmin',
    roleLabel: 'Super Administrador',
    companyLabel: '',
    scopeLabel: 'Global',
    isGlobalScope: true,
  };
}

/** Superadmin global antes de rol operativo por tenant (paridad con Configuración / Gestión de Usuarios). */
function sessionIndicatesGlobalSuperadminScope(dashboardRole?: string | null): boolean {
  if (sessionUserIsCanonicalVaultSuperadmin()) return true;
  if (sessionJwtIndicatesVaultSuperadmin()) return true;

  const fromOperative = resolveSuperadminFromOperativeScope();
  if (fromOperative === true) return true;

  const vaultScope = readVaultIsSuperadminScope();
  if (vaultScope === true) return true;
  if (vaultScope === null && vaultUserIsSuperadminFromSession()) return true;

  const statsRole = (dashboardRole ?? '').trim();
  if (statsRole && operativeRoleIndicatesSuperadmin(statsRole)) return true;

  return false;
}

function operativeRoleIndicatesSuperadmin(role: string): boolean {
  const norm = normalizeRoleToken(role);
  return norm.includes('superadmin') || norm.includes('super_admin') || norm === 'superadministrador';
}

/** Contexto de sesión para dashboard, admin-usuarios y perfil (sync). */
export function vaultResolveActiveTenantContext(opts?: {
  dashboardRole?: string | null;
  tenants?: VaultTenantRoleAssignment[];
}): VaultActiveTenantContext {
  if (sessionIndicatesGlobalSuperadminScope(opts?.dashboardRole)) {
    return buildGlobalSuperadminTenantContext();
  }

  const assignments = collectTenantAssignments(opts?.tenants);
  const isGlobal = readSuperadminScopeFromStorage();

  if (isGlobal) {
    return buildGlobalSuperadminTenantContext();
  }

  const sessionPin = readVaultSessionPin();
  if (
    sessionPin?.role?.trim() &&
    sessionPin.tenant_id?.trim() &&
    !sessionJwtIndicatesVaultSuperadmin()
  ) {
    const pinTenantId =
      normalizeVaultTenantId(sessionPin.tenant_id) || sessionPin.tenant_id.trim();
    const pinRole = sessionPin.role.trim();
    const pinCompany =
      sessionPin.tenant_name?.trim() ||
      resolveCompanyLabel(pinTenantId, assignments, pinRole) ||
      shortVaultTenantId(pinTenantId);
    return {
      tenantId: pinTenantId,
      tenantName: sessionPin.tenant_name?.trim() || pinCompany,
      operativeRole: pinRole,
      roleLabel: operativeRoleLabel(pinRole),
      companyLabel: pinCompany,
      scopeLabel: pinCompany,
      isGlobalScope: false,
    };
  }

  let tenantId = resolveVaultActiveTenantId();
  const dashboardRole = (opts?.dashboardRole ?? '').trim();
  const operativeRole =
    resolveOperativeRoleForTenant(tenantId, assignments) || dashboardRole;

  if (!tenantId && operativeRole) {
    tenantId = tenantIdMatchingOperativeRole(assignments, operativeRole);
  }
  if (!tenantId && VAULT_SINGLE_TENANT_MODE) {
    tenantId = singleTenantIdFromAssignments(assignments);
  }
  if (!tenantId) {
    const firstWithId = assignments.find((a) => (a.tenant_id || '').trim());
    if (firstWithId?.tenant_id) {
      tenantId = normalizeVaultTenantId(firstWithId.tenant_id) || firstWithId.tenant_id.trim();
    }
  }
  const roleLabel = operativeRole
    ? operativeRoleLabel(operativeRole)
    : roleLabelFromDashboardFallback(opts?.dashboardRole);

  let tenantName = resolveCompanyLabel(tenantId, assignments, operativeRole);
  if (!tenantName && VAULT_SINGLE_TENANT_MODE) {
    const withName = assignments.find(
      (row) => (row.tenant_name ?? '').trim() && !isSimappeProductCompanyLabel(row.tenant_name ?? ''),
    );
    if (withName?.tenant_name) {
      tenantName = withName.tenant_name.trim();
      if (!tenantId && withName.tenant_id) {
        tenantId = normalizeVaultTenantId(withName.tenant_id) || withName.tenant_id.trim();
      }
    }
  }
  const validTenantId = normalizeVaultTenantId(tenantId);
  const companyLabel =
    tenantName ||
    (validTenantId ? shortVaultTenantId(validTenantId) : 'Sin empresa');

  return {
    tenantId,
    tenantName,
    operativeRole,
    roleLabel,
    companyLabel,
    scopeLabel: companyLabel,
    isGlobalScope: false,
  };
}
